import type { RefObject } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { EditorRefHandle } from '~/components';

import { RichTextEditor, Title } from '~/components';
import { Field, Input, NumberInput, Select } from '~/components/Form';

import type { JobPostForm } from '../types';

import { useActivityOptions, usePriceUnitOptions } from '../../hooks';
import { sectionStyle, tripleColumnStyle } from './jobPostSections.css';

type Props = {
  editorRef: RefObject<EditorRefHandle | null>;
  form: UseFormReturn<JobPostForm>;
};

export const JobPostBasicSection = ({ editorRef, form }: Props) => {
  const { t } = useTranslation(['jobPosts', 'validation']);

  const { control } = form;

  const activityItems = useActivityOptions();
  const priceUnitItems = usePriceUnitOptions();

  return (
    <div className={sectionStyle}>
      <Title as="h2" styleAs="h3">
        {t('jobPosts:createEdit.sections.basic.title')}
      </Title>

      {/* Title */}
      <Controller
        control={control}
        name="title"
        render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
            <Field.Label>{t('jobPosts:fields.title.label')}</Field.Label>

            <Input onBlur={onBlur} onValueChange={onChange} placeholder={t('jobPosts:fields.title.placeholder')} ref={ref} value={value} />

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{
          maxLength: { value: 200, message: t('validation:maxLength', { attribute: t('jobPosts:fields.title.label'), max: 200 }) },
          required: { value: true, message: t('validation:required') },
          validate: { notEmpty: (value) => !!value.trim() || t('validation:required') },
        }}
      />

      <div className={tripleColumnStyle}>
        {/* Activities */}
        <Controller
          control={control}
          name="activityIds"
          render={({ field: { name, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Field.Label>{t('jobPosts:fields.activities.label')}</Field.Label>

              <Select.Root items={activityItems} multiple onValueChange={onChange} value={value}>
                <Select.Button placeholder={t('jobPosts:fields.activities.placeholder')} />

                <Select.List>
                  {activityItems.map((item) => (
                    <Select.Item key={item.value} value={item.value}>
                      {item.label}
                    </Select.Item>
                  ))}
                </Select.List>
              </Select.Root>

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{ validate: (value) => value.length > 0 || t('validation:required') }}
        />

        {/* Budget */}
        <Controller
          control={control}
          name="price"
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Field.Label optional>{t('jobPosts:fields.price.label')}</Field.Label>

              <NumberInput
                min={0}
                onBlur={onBlur}
                onValueChange={onChange}
                placeholder={t('jobPosts:fields.price.placeholder')}
                prefix="€"
                ref={ref}
                value={value}
              />

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
        />

        {/* Price unit */}
        <Controller
          control={control}
          name="priceUnit"
          render={({ field: { name, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Field.Label>{t('jobPosts:fields.priceUnit.label')}</Field.Label>

              <Select.Root items={priceUnitItems} onValueChange={onChange} value={value}>
                <Select.Button placeholder={t('jobPosts:fields.priceUnit.placeholder')} />

                <Select.List>
                  {priceUnitItems.map((item) => (
                    <Select.Item key={item.value} value={item.value}>
                      {item.label}
                    </Select.Item>
                  ))}
                </Select.List>
              </Select.Root>

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{ required: { value: true, message: t('validation:required') } }}
        />
      </div>

      {/* Description */}
      <Controller
        control={control}
        name="description"
        render={({ field: { name, onBlur, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
            <Field.Label>{t('jobPosts:fields.description.label')}</Field.Label>

            <RichTextEditor isInvalid={invalid} mode="json" onBlur={onBlur} onChange={onChange} ref={editorRef} value={value} />

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={{ validate: { notEmpty: () => !editorRef.current?.isEmpty() || t('validation:required') } }}
      />
    </div>
  );
};
